/**
 * adapters/builtinAdapters.ts — how existing engines join Roamer Core™.
 *
 * Roamer Core coordinates engines, it does not replace them. Each existing
 * engine gets a thin adapter that (a) describes it to the Engine Registry
 * (version, capabilities, health probe) and (b) bridges its native events onto
 * the core event bus. The engines themselves are untouched — coordination is
 * strictly additive.
 */

import { roamerLocationEngine } from '@/services/RoamerLocationEngine';
import { roamerIntelligenceEngine } from '@/services/intelligence';
import { roamerDataFusionEngine } from '@/services/fusion';
import type { FusionEventName } from '@/services/fusion';
import type { CoreEventBus, CoreEventName } from '../events/CoreEventBus';
import type { EngineAdapter } from '../registry/EngineRegistry';

/** The engine surfaces the core orchestrates. Injectable for tests. */
export interface CoreEngineDeps {
  location: typeof roamerLocationEngine;
  intelligence: Pick<typeof roamerIntelligenceEngine, 'clearCache' | 'getDailySuggestions'>;
  fusion: Pick<typeof roamerDataFusionEngine, 'fetch' | 'onAny' | 'getProviderHealth' | 'clearCache'>;
}

export function defaultEngineDeps(): CoreEngineDeps {
  return {
    location: roamerLocationEngine,
    intelligence: roamerIntelligenceEngine,
    fusion: roamerDataFusionEngine,
  };
}

// ─── Registry adapters ─────────────────────────────────────────────────────────

export function buildBuiltinAdapters(deps: CoreEngineDeps): EngineAdapter[] {
  return [
    {
      id: 'location',
      name: 'Roamer Location Engine™',
      version: '1.0.0',
      capabilities: ['current-base', 'location-events', 'gps-snap'],
      healthCheck: () => deps.location.getCurrentLocation() != null,
    },
    {
      id: 'fusion',
      name: 'Roamer Data Fusion Engine™',
      version: '1.0.0',
      capabilities: ['external-data', 'normalization', 'caching', 'provider-fallback'],
      healthCheck: () => Array.isArray(deps.fusion.getProviderHealth()),
    },
    {
      id: 'intelligence',
      name: 'Roamer Intelligence Engine™',
      version: '1.0.0',
      capabilities: ['recommendations', 'roamer-score', 'daily-suggestions', 'explainability'],
      healthCheck: () => true,
    },
  ];
}

// ─── Event bridges (native engine events → core bus) ──────────────────────────

const FUSION_TO_CORE: Partial<Record<FusionEventName, CoreEventName>> = {
  WeatherUpdated: 'WeatherUpdated',
  CurrencyUpdated: 'CurrencyUpdated',
  HousingUpdated: 'HousingUpdated',
  PlacesUpdated: 'PlacesUpdated',
  EventsUpdated: 'EventsUpdated',
  SafetyUpdated: 'SafetyUpdated',
  CommunityUpdated: 'CommunityUpdated',
};

/**
 * Subscribe to each engine's native events and republish them on the core bus.
 * Returns an unbridge function that removes every subscription.
 */
export function bridgeEngineEvents(
  deps: CoreEngineDeps,
  bus: CoreEventBus,
  nowFn: () => number,
): () => void {
  const unsubs: Array<() => void> = [];

  // Location Engine → LocationChanged (event wrapper: use event.current).
  unsubs.push(
    deps.location.onLocationChanged((event) => {
      bus.publish('LocationChanged', 'location', { location: event.current }, nowFn());
    }),
  );

  // Fusion Engine → domain update events.
  unsubs.push(
    deps.fusion.onAny((event) => {
      const coreName = FUSION_TO_CORE[event.name];
      if (!coreName) return;
      bus.publish(coreName, 'fusion', {
        domain: event.domain,
        providerId: event.providerId,
        recordCount: event.recordCount,
      }, nowFn());
    }),
  );

  return () => unsubs.forEach((u) => u());
}
